import { useMutation, useQueryClient } from '@tanstack/react-query';

import { showNotification } from '../../_shared/views/Notifications';
import { LONG_POLLING_INTERVAL } from '../../constants';
import { createSharedState, useSharedState } from '../../hooks/use-shared-state';
import { ErrorNotification, handleError } from '../../services/error-notification';
import { useSseWithPollingFallback } from '../../utils/sse-or-polling';
import { DashboardApi, DashboardData, DashboardDetailWidgetData, DashboardDetailWidgetLayout } from './dashboard-api';
import {
  DashboardDetail,
  asUpdateDashboardWidget,
  initialDashboardDetail,
  setDashboardWidgetToRemove,
  updateDashboardDetail,
  updateDashboardDetailWidth,
  updateDashboardShowLines,
} from './models/dashboard-detail';
import {
  DashboardDetailWidget,
  addDashboardEmptyWidget,
  removeDashboardWidget,
  updateDashboardWidgetPositions,
} from './models/dashboard-detail-widget';
import type { DashboardRemove, DashboardUpsert } from './models/dashboard-list';
import { renameDashboardDetailWidget } from './models/dashboard-widget-rename';
import { loadWidgetSelectApp, updateDashboardDetailWidgetService } from './models/dashboard-widget-select-app';

const api = new DashboardApi();

// Saves are applied to the local state before the request, so incoming server state is ignored while any
// is in flight, otherwise it would revert changes that have not been persisted yet.
const pendingSavesState = createSharedState(0);

function saveChange(run: () => Promise<ErrorNotification | undefined>) {
  pendingSavesState.set(pendingSavesState.get() + 1);
  return run()
    .finally(() => {
      pendingSavesState.set(pendingSavesState.get() - 1);
    })
    .then(handleError);
}

export function useIsDashboardSaving() {
  const [pendingSaves] = useSharedState(pendingSavesState);
  return pendingSaves > 0;
}

export function useDashboardsSSE() {
  const queryClient = useQueryClient();

  const { data: dashboards, isLoading } = useSseWithPollingFallback<DashboardData[]>({
    queryKey: ['dashboards'],
    subscribeSSE: () =>
      api.getDashboardsSSE((data) => {
        queryClient.setQueryData(['dashboards'], data);
      }),
    fetchFallbackFn: async () => {
      const data = await api.getDashboards();
      return data;
    },
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { dashboards, isLoading };
}

export function useDashboardDetailSSE(dashboardId: string) {
  const queryClient = useQueryClient();

  const { data: dashboardDetail, isLoading } = useSseWithPollingFallback<DashboardDetail>({
    queryKey: ['dashboard-detail', dashboardId],
    subscribeSSE: () =>
      api.getDashboardDetailSSE(dashboardId, (data) => {
        if (pendingSavesState.get() > 0) {
          return;
        }

        const currentData = queryClient.getQueryData<DashboardDetail>(['dashboard-detail', dashboardId]) ?? initialDashboardDetail();
        const currentDashboard = updateDashboardDetail(currentData, data);
        queryClient.setQueryData(['dashboard-detail', dashboardId], currentDashboard);
      }),
    fetchFallbackFn: async () => {
      const initialDashboard = initialDashboardDetail();
      const currentDashboard = queryClient.getQueryData<DashboardDetail>(['dashboard-detail', dashboardId]) ?? initialDashboard;

      if (pendingSavesState.get() > 0) {
        return currentDashboard;
      }

      const data = await api.getDashboardDetail(dashboardId);
      if (!data) {
        handleError({ message: 'Error loading dashboard.', deduplicationKey: `dashboard-detail-${dashboardId}` });
        return currentDashboard;
      }

      return updateDashboardDetail(currentDashboard, data);
    },
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { dashboardDetail, isLoading };
}

// Mutations

export function useUpsertDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: DashboardUpsert & { name: string }) =>
      model.id ? api.updateDashboard(model.id, { name: model.name }) : api.createDashboard({ name: model.name }),
    onSuccess: (res, model) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: 'Error saving dashboard' });
        return;
      }

      if (!model.id) {
        void qc.invalidateQueries({ queryKey: ['dashboards'] });
        return;
      }

      // Optimistic update:
      qc.setQueryData<DashboardData[]>(['dashboards'], (old) => old?.map((d) => (d.id === model.id ? { ...d, name: model.name } : d)));
    },
  });
}

export function useRemoveDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: DashboardRemove) => api.deleteDashboard(model.dashboardId),
    onSuccess: (res, model) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: 'Error removing dashboard' });
        return;
      }
      // Optimistic update:
      qc.setQueryData<DashboardData[]>(['dashboards'], (old) => old?.filter((d) => d.id !== model.dashboardId));
    },
  });
}

export function useDashboardDetailActions(dashboardId: string) {
  const qc = useQueryClient();
  const getModel = () => qc.getQueryData<DashboardDetail>(['dashboard-detail', dashboardId]) ?? initialDashboardDetail();
  const update = (model: DashboardDetail) => qc.setQueryData(['dashboard-detail', dashboardId], model);

  return {
    onWidgetAdd: () => saveChange(() => addDashboardEmptyWidget(api, getModel, update)),
    onWidgetPositionsChanged: (positions: DashboardDetailWidgetLayout[]) =>
      saveChange(() => updateDashboardWidgetPositions(positions, api, getModel, update)),

    onToggleGrid: () => update(updateDashboardShowLines(getModel(), !getModel().showWidgetGrid)),
    onSizeChanged: (point: { width: number; height: number }) => update(updateDashboardDetailWidth(getModel(), point.width)),
  };
}

export function useDashboardWidgetActions(dashboardId: string) {
  const qc = useQueryClient();
  const getModel = () => qc.getQueryData<DashboardDetail>(['dashboard-detail', dashboardId]) ?? initialDashboardDetail();
  const update = (model: DashboardDetail) => qc.setQueryData(['dashboard-detail', dashboardId], model);
  const updateWidget = asUpdateDashboardWidget(getModel, update);

  return {
    onRenameStart: (widget: DashboardDetailWidget) =>
      updateWidget({ id: widget.id, rename: { dashboardId, widgetId: widget.id, name: widget.name ?? '' } }),
    onRenameDone: (widgetId: string, name: string | false) =>
      name === false
        ? updateWidget({ id: widgetId, rename: undefined })
        : saveChange(() => renameDashboardDetailWidget(widgetId, name, api, getModel, update)),

    onSelectServiceStart: (widget: DashboardDetailWidget) =>
      loadWidgetSelectApp(api, dashboardId, widget, (selectApp) => updateWidget({ id: widget.id, selectApp })).then(handleError),
    onSelectServiceCancel: (widgetId: string) => updateWidget({ id: widgetId, selectApp: undefined }),
    onSelectServiceDone: (widgetId: string, type: string, serviceId: string) =>
      saveChange(() => updateDashboardDetailWidgetService(widgetId, type, serviceId, api, getModel, update)),

    onRemoveStart: (widget: DashboardDetailWidgetData) => update(setDashboardWidgetToRemove(getModel(), widget)),
    onRemoveDone: (remove: boolean) =>
      remove ? saveChange(() => removeDashboardWidget(api, getModel, update)) : update(setDashboardWidgetToRemove(getModel(), undefined)),
  };
}
